import type { EmailCategory, EmailPreferenceChanges, EmailPreferencesView } from '../../services/email-preferences.service'
import { useMutation, useQuery } from '@tanstack/react-query'
import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Form, Spinner } from 'react-bootstrap'
import { useLocation } from 'react-router-dom'
import Button from '../../components/Button'
import {
  EMAIL_CATEGORIES,
  EmailPreferencesApiError,
  getEmailPreferences,
  unsubscribeEmailCategory,
  updateEmailPreferences,
} from '../../services/email-preferences.service'
import { setWindowClass } from '../../util/helpers'

/**
 * Public page behind the "Afmelden" / "E-mailvoorkeuren beheren" links in
 * our customer mails. No session: the signed token in the URL identifies
 * the CRM contact and is the whole authorisation (see the backend's
 * EmailPreferencesService). `?afmelden=<category>` pre-selects a one-click
 * unsubscribe; the page never changes anything on load, so link prefetchers
 * cannot unsubscribe anyone.
 */
function EmailVoorkeuren() {
  const location = useLocation()
  const { token, afmelden } = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const category = params.get('afmelden')
    return {
      token: params.get('token') ?? '',
      afmelden: EMAIL_CATEGORIES.includes(category as EmailCategory) ? category as EmailCategory : null,
    }
  }, [location.search])

  const [view, setView] = useState<EmailPreferencesView | null>(null)
  // Switch state: true = ontvangt
  const [receives, setReceives] = useState<Record<string, boolean>>({})
  const [unsubscribed, setUnsubscribed] = useState<EmailCategory | null>(null)
  const [saved, setSaved] = useState(false)

  const query = useQuery({
    queryKey: ['email-voorkeuren', token],
    queryFn: () => getEmailPreferences(token),
    enabled: !!token,
    retry: false,
  })

  const applyView = (next: EmailPreferencesView) => {
    setView(next)
    setReceives(Object.fromEntries(next.categories.map(category => [category.key, category.receives])))
  }

  useEffect(() => {
    if (query.data)
      applyView(query.data)
  }, [query.data])

  const unsubscribe = useMutation({
    mutationFn: (category: EmailCategory) => unsubscribeEmailCategory(token, category),
    onSuccess: (next, category) => {
      applyView(next)
      setUnsubscribed(category)
      setSaved(false)
    },
  })

  const save = useMutation({
    mutationFn: (changes: EmailPreferenceChanges) => updateEmailPreferences(token, changes),
    onSuccess: (next) => {
      applyView(next)
      setUnsubscribed(null)
      setSaved(true)
    },
  })

  setWindowClass('hold-transition login-page bg-body-secondary')

  const changes: EmailPreferenceChanges = {}
  for (const category of view?.categories ?? []) {
    if (receives[category.key] !== category.receives)
      changes[category.key] = receives[category.key]
  }
  const dirty = Object.keys(changes).length > 0

  const pendingUnsubscribe = view && afmelden && !unsubscribed
    ? view.categories.find(category => category.key === afmelden && category.receives) ?? null
    : null

  const errorMessage = (error: unknown) => {
    if (error instanceof EmailPreferencesApiError && error.status === 404)
      return 'Deze link is niet (meer) geldig. Gebruik de link uit een recente e-mail of neem contact op met onze servicedesk.'
    return error instanceof Error && error.message ? error.message : 'Er is iets misgegaan. Probeer het later opnieuw.'
  }

  return (
    <div className="login-box" style={{ width: 'min(560px, 94vw)' }}>
      <div className="card card-outline card-primary">
        <div className="card-header text-center">
          <span className="h2">
            <b>DATAFIBER</b>
            <span>NETWORK</span>
          </span>
        </div>
        <div className="card-body">
          <h5 className="mb-1">E-mailvoorkeuren</h5>
          <p className="text-muted small mb-3">E-mail preferences</p>

          {!token && (
            <Alert variant="danger">
              Deze link is onvolledig. Gebruik de link uit een recente e-mail van DataFiber.
              <div className="small mt-1">This link is incomplete. Please use the link from a recent DataFiber e-mail.</div>
            </Alert>
          )}

          {token && query.isLoading && (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" className="me-2" />
              Voorkeuren laden...
            </div>
          )}

          {token && query.isError && (
            <Alert variant="danger">
              {errorMessage(query.error)}
              <div className="small mt-1">This link is not (or no longer) valid. Please use the link from a recent e-mail or contact our service desk.</div>
            </Alert>
          )}

          {view && (
            <>
              <p className="mb-3">
                Voorkeuren van
                {' '}
                <strong>
                  {view.contact.voornaam}
                  {' '}
                  {view.contact.achternaam}
                </strong>
                {' '}
                (
                {view.contact.email}
                )
                {view.contact.bedrijf && (
                  <>
                    , contactpersoon van
                    {' '}
                    <strong>{view.contact.bedrijf}</strong>
                  </>
                )}
                .
              </p>

              {view.emailOptOut && (
                <Alert variant="warning">
                  In ons CRM staat u geregistreerd als &quot;geen e-mail&quot;: u ontvangt geen enkele geautomatiseerde e-mail van DataFiber.
                  Wilt u dit wijzigen, neem dan contact op met onze servicedesk.
                  <div className="small mt-1">Our CRM lists you as &quot;no e-mail&quot;: you receive no automated e-mail from DataFiber at all. Contact our service desk to change this.</div>
                </Alert>
              )}

              {pendingUnsubscribe && (
                <Alert variant="primary">
                  <p className="mb-2">
                    Wilt u geen e-mails over
                    {' '}
                    <strong>{pendingUnsubscribe.label.toLowerCase()}</strong>
                    {' '}
                    meer ontvangen?
                    <span className="d-block small">
                      No longer want e-mails about
                      {' '}
                      {pendingUnsubscribe.labelEn}
                      ?
                    </span>
                  </p>
                  <Button
                    size="sm"
                    isLoading={unsubscribe.isPending}
                    onClick={() => unsubscribe.mutate(pendingUnsubscribe.key)}
                  >
                    Ja, afmelden / Yes, unsubscribe
                  </Button>
                </Alert>
              )}

              {unsubscribe.isError && (
                <Alert variant="danger">{errorMessage(unsubscribe.error)}</Alert>
              )}

              {unsubscribed && (
                <Alert variant="success">
                  U bent afgemeld voor e-mails over
                  {' '}
                  {view.categories.find(category => category.key === unsubscribed)?.label.toLowerCase()}
                  .
                  <div className="small mt-1">
                    You have been unsubscribed from e-mails about
                    {' '}
                    {view.categories.find(category => category.key === unsubscribed)?.labelEn}
                    .
                  </div>
                </Alert>
              )}

              <Form
                onSubmit={(event) => {
                  event.preventDefault()
                  if (dirty)
                    save.mutate(changes)
                }}
              >
                <fieldset disabled={view.emailOptOut || save.isPending}>
                  {view.categories.map(category => (
                    <div key={category.key} className="d-flex align-items-start justify-content-between border-top py-2">
                      <div className="me-3">
                        <div className="fw-bold">{category.label}</div>
                        <div className="small text-muted">{category.description}</div>
                        <div className="small text-muted fst-italic">{category.descriptionEn}</div>
                      </div>
                      <Form.Check
                        type="switch"
                        id={`voorkeur-${category.key}`}
                        label={receives[category.key] && !view.emailOptOut ? 'Aan' : 'Uit'}
                        checked={!!receives[category.key] && !view.emailOptOut}
                        onChange={(event) => {
                          setSaved(false)
                          setReceives(current => ({ ...current, [category.key]: event.target.checked }))
                        }}
                      />
                    </div>
                  ))}
                </fieldset>

                {save.isError && (
                  <Alert variant="danger" className="mt-3 mb-0">{errorMessage(save.error)}</Alert>
                )}
                {saved && !dirty && (
                  <Alert variant="success" className="mt-3 mb-0">
                    Uw voorkeuren zijn opgeslagen.
                    <div className="small">Your preferences have been saved.</div>
                  </Alert>
                )}

                <div className="d-grid mt-3">
                  <Button type="submit" disabled={!dirty || view.emailOptOut} isLoading={save.isPending}>
                    Voorkeuren opslaan / Save preferences
                  </Button>
                </div>
              </Form>
            </>
          )}

          <p className="text-muted small mt-3 mb-0">
            Vragen? DataFiber Telecom BV · servicedesk@datafiber.nl · +31 (0)79 7600 320
          </p>
        </div>
      </div>
    </div>
  )
}

export default EmailVoorkeuren
